import React from 'react';
import { Button, Card, Progress, Space, Tag, Tooltip, Typography, message } from 'antd';
import { CheckCircleFilled, CloseCircleOutlined, LockOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import { fmtNum } from './common';
import { useAuth } from '../../../../shared/hooks/useAuth';
import authService from '../../../../shared/services/authService';

const { Text } = Typography;

const LEVEL_COLOR = ['red', 'default', 'blue', 'cyan', 'green', 'gold', 'purple'];

export const LevelTag = ({ level }) => (
    <Tag color={LEVEL_COLOR[level] || 'default'} style={{ marginInlineEnd: 0, fontWeight: 600 }}>Lv{level}</Tag>
);

const pct = (v) => `${+(Number(v) * 100).toFixed(3)}%`;

/** 升级条件一行：后端 key → 中文源串（t() 翻译），current/required 原样展示 */
const ReqLabel = ({ r }) => {
    const { t } = useTranslation();
    switch (r.key) {
        case 'email_verified': return t('Account email verified');
        case 'verified_domain': return t('At least 1 verified sending domain');
        case 'account_age': return `${t('Account at least {{n}} days old', { n: r.required })}（${fmtNum(r.current)}）`;
        case 'delivered': return `${t('Total delivered ≥ {{n}}', { n: fmtNum(r.required) })}（${fmtNum(r.current)}）`;
        case 'paid': return t('At least one payment made');
        case 'hard_bounce_rate': return `${t('Hard bounce rate < {{p}} over the last {{d}} days', { d: r.window_days, p: pct(r.required) })}（${pct(r.current)}）`;
        case 'complaint_rate': return `${t('Complaint rate < {{p}} over the last {{d}} days', { d: r.window_days, p: pct(r.required) })}（${pct(r.current)}）`;
        default: return r.label;
    }
};

/**
 * 账号信用等级卡片（发信流列表页）。trust = GET /api/user/tm/trust；streams 用来判断有没有免费 / 付费流。
 * 免费流：显示本月等级额度用量；付费流：一行提示等级系数影响发信速率。
 */
const TrustCard = ({ trust, streams }) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const resendEmail = async () => {
        const r = await authService.resendVerification(currentUser?.email);
        if (r?.success === false) message.error(r.message || t('Operation failed'));
        else message.success(t('Verification email sent'));
    };
    if (!trust) return null;
    const customer = (streams || []).filter((s) => s.trust);
    const hasFree = trust.allowance_used !== null && trust.allowance_used !== undefined;
    const hasPaid = customer.some((s) => !s.trust.free_plan);
    const used = trust.allowance_used || 0;
    const total = trust.allowance_month || 0;
    const next = trust.next_level;

    return (
        <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 180 }}>
                    <Space size={8} align="center">
                        <Text type="secondary">{t('Trust level')}</Text>
                        <LevelTag level={trust.level} />
                        {trust.locked && <Tooltip title={t('Level locked by an administrator')}><LockOutlined /></Tooltip>}
                    </Space>
                    {hasFree && (
                        <div style={{ marginTop: 10, maxWidth: 260 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, gap: 8 }}>
                                <Space size={4}>
                                    <Text type="secondary">{t('Free plan allowance this month')}</Text>
                                    <Tooltip title={t('The free plan\'s monthly allowance and hourly cap depend on your credit level; higher levels get more')}>
                                        <QuestionCircleOutlined style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary, #999)' }} />
                                    </Tooltip>
                                </Space>
                                <Text>{fmtNum(used)} / {fmtNum(total)}</Text>
                            </div>
                            <Progress percent={total ? Math.min(100, Math.round((used / total) * 100)) : 100} showInfo={false} size="small"
                                status={used >= total ? 'exception' : 'normal'} />
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('{{n}} emails per hour', { n: fmtNum(trust.hourly_free) })}</Text>
                        </div>
                    )}
                    {hasPaid && (
                        <div style={{ marginTop: 8 }}>
                            <Tooltip title={t('Your level sets the sending rate on paid plans: hourly cap = plan cap × level factor')}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t('Sending rate')} ×{trust.hourly_factor} <QuestionCircleOutlined style={{ fontSize: 12 }} />
                                </Text>
                            </Tooltip>
                        </div>
                    )}
                    {trust.promotion_blocked === 'cooldown' && trust.cooldown_until && (
                        <div style={{ marginTop: 6 }}>
                            <Text type="warning" style={{ fontSize: 12 }}>{t('Downgrade cooldown until {{d}}', { d: String(trust.cooldown_until).slice(0, 10) })}</Text>
                        </div>
                    )}
                </div>
                {next && trust.promotion_blocked !== 'level_0' && (
                    <div style={{ flex: 1, minWidth: 240 }}>
                        <Space size={6} style={{ marginBottom: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Next level')}</Text>
                            <LevelTag level={next.level} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('Free plan {{n}} emails/month', { n: fmtNum(next.allowance) })} · ×{next.hourly_factor}
                            </Text>
                        </Space>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {(next.requirements || []).map((r) => (
                                <Text key={r.key} style={{ fontSize: 12 }} type={r.met ? undefined : 'danger'}>
                                    {r.met
                                        ? <CheckCircleFilled style={{ color: '#52c41a', marginInlineEnd: 6 }} />
                                        : <CloseCircleOutlined style={{ marginInlineEnd: 6 }} />}
                                    <ReqLabel r={r} />
                                    {!r.met && r.key === 'email_verified' && (
                                        <Button type="link" size="small" style={{ fontSize: 12, height: 'auto', padding: '0 6px' }} onClick={resendEmail}>
                                            {t('Resend verification email')}
                                        </Button>
                                    )}
                                </Text>
                            ))}
                        </div>
                    </div>
                )}
                {trust.promotion_blocked === 'level_0' && (
                    <Text type="danger" style={{ fontSize: 12 }}>{t('Your account credit level is Lv0 and sending is stopped. Please contact support.')}</Text>
                )}
            </div>
        </Card>
    );
};

export default TrustCard;
